import React from "react";
import { Box, Skeleton, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ClientBand, ClientCard, ClientPage } from "./ClientUiViewClient";

// Esqueleto del inicio mientras llegan los datos: la misma disposición que la
// página (empresa, gráfica con las cifras a su derecha y la tabla de campañas),
// para que al cargar nada salte de sitio.

export const Bone: React.FC<{
  width?: number | string;
  height: number | string;
  radius?: number | string;
}> = ({ width = "100%", height, radius = 999 }) => (
  <Skeleton
    variant="rectangular"
    animation="wave"
    width={width}
    height={height}
    sx={{ borderRadius: radius, flexShrink: 0 }}
  />
);

/** Una cifra de la columna derecha: etiqueta, número y sus barras. */
const TileBone: React.FC<{ withChart?: boolean }> = ({ withChart = true }) => (
  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Bone width={84} height={14} />
      <Bone width={120} height={36} radius={10} />
    </Box>
    {withChart && <Bone width={96} height={56} radius={10} />}
  </Box>
);

/** Fila de la tabla de campañas. */
const RowBone: React.FC<{ nameWidth: string }> = ({ nameWidth }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns:
          "minmax(0, 1fr) 110px 132px 104px 104px 110px 44px",
        columnGap: 2,
        alignItems: "center",
        px: 2.5,
        py: 2.75,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Bone width={nameWidth} height={16} />
      <Bone width={64} height={14} />
      <Bone width={84} height={26} />
      <Bone width={84} height={14} />
      <Bone width={84} height={14} />
      <Bone width={64} height={14} />
      <Bone width={20} height={20} />
    </Box>
  );
};

const HomeSkeletonViewClient: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box role="status" aria-busy="true" aria-label={t("clientView.loading")}>
      <ClientPage
        wide
        band={
          <ClientBand title={<Bone width={180} height={20} />}>
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: 860 }}>
                {["62%", "48%", "55%", "40%"].map((width) => (
                  <RowBone key={width} nameWidth={width} />
                ))}
              </Box>
            </Box>
          </ClientBand>
        }
      >
        <Bone width={220} height={40} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              lg: "minmax(0, 1fr) 280px",
            },
            gap: { xs: 4, lg: 10 },
            alignItems: "center",
          }}
        >
          <Box sx={{ minWidth: 0, order: { xs: 2, lg: 1 } }}>
            <ClientCard title={<Bone width={260} height={22} />}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 4 }}>
                <Bone width={300} height={38} />
                <Bone width={170} height={38} />
                <Bone width={240} height={38} />
              </Box>
              <Bone height={320} radius={16} />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 3 }}>
                {[150, 130, 170, 120].map((width) => (
                  <Bone key={width} width={width} height={14} />
                ))}
              </Box>
            </ClientCard>
          </Box>
          <Box
            sx={{
              order: { xs: 1, lg: 2 },
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "minmax(0, 1fr)",
              },
              gap: { xs: 3, lg: 4 },
            }}
          >
            <TileBone />
            <TileBone />
            <TileBone />
            <TileBone withChart={false} />
          </Box>
        </Box>
      </ClientPage>
    </Box>
  );
};

export default HomeSkeletonViewClient;
